/**
 * PasswordInput — text input with a built-in show/hide eye toggle.
 *
 * Default behavior: renders as type="password" with an Eye icon button on the
 * right. Clicking the eye flips to type="text" + an EyeOff icon. Tab order
 * preserves: input → eye → next-field, and the eye is tabbable but has its
 * own visible focus ring. The toggle is a real <button type="button"> so it
 * never accidentally submits the surrounding <form>.
 *
 * Props mirror a plain <input>: value, onChange, placeholder, minLength,
 * required, autoComplete, plus an optional `testId` to wire e2e tests.
 *
 * Visibility lifts to React state inside this component — each instance has
 * its own independent show/hide. For "Password" + "Confirm password" pairs on
 * the same form, that's the desired UX (you can peek one without revealing
 * the other, e.g. when checking what you typed in just the confirm field).
 */
import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

export default function PasswordInput({
  value,
  onChange,
  placeholder,
  minLength = 6,
  required = true,
  autoComplete = "current-password",
  testId,
  ariaLabel,
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        type={visible ? "text" : "password"}
        required={required}
        minLength={minLength}
        value={value}
        onChange={onChange}
        className="rm-input pr-12"
        data-testid={testId}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-label={ariaLabel}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1"
        style={{ color: "#475569" }}
        data-testid={testId ? `${testId}-toggle` : "password-visibility-toggle"}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        tabIndex={0}
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}
